import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  static DESKTOP_AUTO_OPEN_DELAY = 550;

  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | undefined} */
  #autoOpenTimeout;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {HTMLElement | null} */
  #returnFocusElement = null;

  /** @type {string | null} */
  #defaultPendingCopy = null;

  /** @type {number} */
  #cartCount = 0;

  /** @type {string} */
  #cartSubtotal = '';

  /** @type {HTMLDivElement | null} */
  #stickyCheckoutBar = null;

  /** @type {MutationObserver | null} */
  #stickyCheckoutObserver = null;

  /** @type {MutationObserver | null} */
  #overlayObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('resize', this.#handleViewportChange);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#defaultPendingCopy = this.querySelector('.cart-drawer__pending-copy')?.textContent?.trim() || null;
    this.#cartCount = this.#readCartCountFromDom() ?? (Number(this.dataset.initialCartCount) || 0);
    this.#cartSubtotal = this.#readCartSubtotalFromDom();
    this.#mountStickyCheckoutBar();
    this.#observeStickyCheckoutBar();
    this.#observeViewportOverlays();
    this.#syncStickyCheckoutBar();
    this.#setStickyCheckoutVisible(this.#shouldShowStickyCheckoutBar());
    this.#updateStickyCheckoutPosition();
    this.syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('resize', this.#handleViewportChange);
    this.#historyAbortController?.abort();
    this.#clearScheduledAutoOpen();
    this.#stickyCheckoutObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#teardownStickyCheckoutBar();
    removeTrapFocus();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart updates and keeps the mobile sticky checkout bar synced with cart count and totals.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean, source?: string, itemCount?: number } }>} event
   */
  #handleCartUpdate = (event) => {
    const didError = event.detail.data?.didError === true;
    const cameFromProductForm = event.detail.data?.source === 'product-form-component';
    const isMobileProductFormAdd = cameFromProductForm && isMobileBreakpoint();
    const shouldDelayAutoOpen = cameFromProductForm;
    const shouldAutoOpen = this.hasAttribute('auto-open') || (cameFromProductForm && !isMobileBreakpoint());
    const cartCount = this.#resolveCartCount(event);
    const cartSubtotal = this.#resolveCartSubtotal(event);

    if (didError) {
      this.#clearScheduledAutoOpen();
      this.#resetPendingPreview();
      this.#announceCartCount(cartCount);
      this.#setStickyCheckoutVisible(this.#shouldShowStickyCheckoutBar());
      return;
    }

    if (typeof cartCount === 'number') {
      this.#setCartCount(cartCount);
    }

    if (cartSubtotal) {
      this.#cartSubtotal = cartSubtotal;
    }

    this.#syncStickyCheckoutBar();
    this.#setStickyCheckoutVisible(this.#shouldShowStickyCheckoutBar());

    if (isMobileProductFormAdd) {
      this.#clearScheduledAutoOpen();
      this.#resetPendingPreview();
      this.#setStickyCheckoutVisible(true);
      return;
    }

    if (!cameFromProductForm) return;

    this.#renderPendingPreview(event.detail.resource);

    if (shouldAutoOpen) {
      this.#setStickyCheckoutVisible(false);
      if (shouldDelayAutoOpen) {
        this.#scheduleAutoOpen(
          cartCount,
          CartDrawerComponent.DESKTOP_AUTO_OPEN_DELAY
        );
      } else {
        this.#openFromCartAdd(cartCount);
      }
      return;
    }

    this.#announceCartCount(cartCount);
  };

  /**
   * Schedules the cart drawer to open after the add-to-cart success animation finishes.
   * @param {number | undefined} cartCount
   * @param {number} delay
   */
  #scheduleAutoOpen(cartCount, delay) {
    this.#clearScheduledAutoOpen();

    this.#autoOpenTimeout = window.setTimeout(() => {
      this.#autoOpenTimeout = undefined;
      this.#openFromCartAdd(cartCount);
    }, delay);
  }

  #clearScheduledAutoOpen() {
    if (this.#autoOpenTimeout) {
      window.clearTimeout(this.#autoOpenTimeout);
      this.#autoOpenTimeout = undefined;
    }
  }

  /**
   * Opens the drawer using the existing cart-add flow and announces the updated cart count.
   * @param {number | undefined} cartCount
   */
  #openFromCartAdd(cartCount) {
    this.#clearScheduledAutoOpen();
    this.#captureReturnFocus();
    this.#setStickyCheckoutVisible(false);
    this.open();
    this.dataset.openSource = 'cart-add';

    requestAnimationFrame(() => {
      this.#announceCartCount(cartCount);
    });
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#clearScheduledAutoOpen();
    this.#captureReturnFocus();
    this.#setStickyCheckoutVisible(false);
    this.dataset.openSource = 'manual';
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#clearScheduledAutoOpen();
    this.closeDialog();
  }

  #captureReturnFocus() {
    if (document.activeElement instanceof HTMLElement) {
      this.#returnFocusElement = document.activeElement;
    }
  }

  #handleDialogOpen = () => {
    this.syncState();
    this.#setStickyCheckoutVisible(false);

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    requestAnimationFrame(() => {
      const preferredFocusTarget = dialog.querySelector(
        '.cart-drawer__close-button, [name="checkout"], .cart-discount__input, .quantity-selector input, button, a[href]'
      );

      trapFocus(dialog);

      if (preferredFocusTarget instanceof HTMLElement) {
        preferredFocusTarget.focus({ preventScroll: true });
      } else {
        dialog.focus({ preventScroll: true });
      }
    });
  };

  #handleDialogClose = () => {
    removeTrapFocus();

    if (this.#returnFocusElement?.isConnected) {
      requestAnimationFrame(() => {
        this.#returnFocusElement?.focus({ preventScroll: true });
      });
    }

    this.#returnFocusElement = null;
    delete this.dataset.openSource;
    this.#resetPendingPreview();
    this.#setStickyCheckoutVisible(this.#shouldShowStickyCheckoutBar());
  };

  syncState() {
    const dialog = this.refs?.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const isPending = this.getAttribute('data-loading') === 'true';
    const hasCartForm = !!dialog.querySelector('.cart-form');
    const labelledBy = isPending ? 'cart-drawer-heading-pending' : hasCartForm ? 'cart-drawer-heading' : 'cart-drawer-heading-empty';

    dialog.classList.toggle('cart-drawer--empty', !hasCartForm && !isPending);
    dialog.classList.toggle('cart-drawer--pending', isPending);
    dialog.setAttribute('aria-labelledby', labelledBy);

    if (!isPending) {
      this.#resetPendingPreview();
    }
  }

  previewPending(resource) {
    this.#renderPendingPreview(resource);
  }

  #renderPendingPreview(resource) {
    const lineItems = this.#extractLineItems(resource);
    if (lineItems.length === 0) return;

    const pendingItems = this.querySelector('.cart-drawer__pending-items');
    const pendingSummary = this.querySelector('.cart-drawer__pending-summary');
    const pendingCopy = this.querySelector('.cart-drawer__pending-copy');

    if (!pendingItems || !pendingSummary || !pendingCopy) return;

    const totalQuantity = lineItems.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const quantityLabel = totalQuantity === 1 ? 'item' : 'items';

    pendingCopy.textContent = `${totalQuantity} ${quantityLabel} added. Confirming totals and checkout details now.`;
    pendingItems.innerHTML = lineItems
      .slice(0, 2)
      .map((item) => {
        const imageMarkup = item.image
          ? `<img class="cart-drawer__pending-preview-image" src="${item.image}" alt="${this.#escapeHtml(item.title)}" loading="eager" decoding="async">`
          : '<div class="cart-drawer__pending-media"></div>';
        const meta = [item.variantTitle, `Qty ${item.quantity}`].filter(Boolean).join(' • ');

        return `
          <article class="cart-drawer__pending-preview">
            <div class="cart-drawer__pending-preview-media">${imageMarkup}</div>
            <div class="cart-drawer__pending-preview-details">
              <p class="cart-drawer__pending-preview-title">${this.#escapeHtml(item.title)}</p>
              ${meta ? `<p class="cart-drawer__pending-preview-meta">${this.#escapeHtml(meta)}</p>` : ''}
              <p class="cart-drawer__pending-preview-price">${this.#formatMoney(item.price * item.quantity)}</p>
            </div>
          </article>
        `;
      })
      .join('');

    pendingSummary.innerHTML = `
      <div class="cart-drawer__pending-summary-row">
        <span>Subtotal</span>
        <strong>${this.#formatMoney(subtotal)}</strong>
      </div>
      <div class="cart-drawer__pending-summary-row">
        <span>Total</span>
        <strong>${this.#formatMoney(subtotal)}</strong>
      </div>
      <span class="cart-drawer__pending-button"></span>
    `;
  }

  #resetPendingPreview() {
    const pendingItems = this.querySelector('.cart-drawer__pending-items');
    const pendingSummary = this.querySelector('.cart-drawer__pending-summary');
    const pendingCopy = this.querySelector('.cart-drawer__pending-copy');

    if (pendingCopy && this.#defaultPendingCopy) {
      pendingCopy.textContent = this.#defaultPendingCopy;
    }

    if (pendingItems) {
      pendingItems.innerHTML = `
        <div class="cart-drawer__pending-item">
          <div class="cart-drawer__pending-media"></div>
          <div class="cart-drawer__pending-lines">
            <span class="cart-drawer__pending-line cart-drawer__pending-line--title"></span>
            <span class="cart-drawer__pending-line cart-drawer__pending-line--meta"></span>
            <span class="cart-drawer__pending-line cart-drawer__pending-line--price"></span>
          </div>
        </div>
        <div class="cart-drawer__pending-item">
          <div class="cart-drawer__pending-media"></div>
          <div class="cart-drawer__pending-lines">
            <span class="cart-drawer__pending-line cart-drawer__pending-line--title"></span>
            <span class="cart-drawer__pending-line cart-drawer__pending-line--meta"></span>
            <span class="cart-drawer__pending-line cart-drawer__pending-line--price"></span>
          </div>
        </div>
      `;
    }

    if (pendingSummary) {
      pendingSummary.innerHTML = `
        <span class="cart-drawer__pending-line cart-drawer__pending-line--summary"></span>
        <span class="cart-drawer__pending-line cart-drawer__pending-line--summary cart-drawer__pending-line--summary-short"></span>
        <span class="cart-drawer__pending-button"></span>
      `;
    }
  }

  #extractLineItems(resource) {
    if (!resource || typeof resource !== 'object') return [];

    const rawItems = Array.isArray(resource.items) ? resource.items : [resource];

    return rawItems
      .map((item) => {
        if (!item || typeof item !== 'object') return null;

        const title = item.product_title || item.title || item.name;
        const quantity = Number(item.quantity) || 0;
        const linePrice = Number(item.final_line_price ?? item.line_price) || 0;
        const unitPrice = Number(item.final_price ?? item.price) || (quantity > 0 ? Math.round(linePrice / quantity) : 0);
        const image =
          item.featured_image?.url ||
          item.image ||
          item.image_url ||
          item.featured_image?.src ||
          item.featured_image?.preview_image?.src ||
          '';

        if (!title || quantity <= 0) return null;

        return {
          title,
          quantity,
          price: unitPrice,
          image,
          variantTitle: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        };
      })
      .filter(Boolean);
  }

  #formatMoney(value) {
    const currencyCode = Shopify.currency?.active || 'USD';

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(value / 100);
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  #getStickyCheckoutBar() {
    if (this.#stickyCheckoutBar?.isConnected) return this.#stickyCheckoutBar;

    const bar = document.querySelector('.cart-drawer__sticky-checkout');
    this.#stickyCheckoutBar = bar instanceof HTMLDivElement ? bar : null;

    return this.#stickyCheckoutBar;
  }

  #shouldShowStickyCheckoutBar() {
    return isMobileBreakpoint() && this.#cartCount > 0 && !this.refs.dialog?.open;
  }

  #setStickyCheckoutVisible(isVisible) {
    const stickyCheckoutBar = this.#getStickyCheckoutBar();
    const shouldShow = Boolean(isVisible && this.#shouldShowStickyCheckoutBar());
    if (!stickyCheckoutBar) return;

    stickyCheckoutBar.hidden = !shouldShow;
    stickyCheckoutBar.dataset.visible = shouldShow ? 'true' : 'false';
    this.#updateStickyCheckoutPosition();
  }

  #syncStickyCheckoutBar() {
    const stickyCheckoutBar = this.#getStickyCheckoutBar();
    if (!stickyCheckoutBar) return;

    const countLabel = this.#cartCount === 1 ? '1 item' : `${this.#cartCount} items`;
    const countElement = stickyCheckoutBar.querySelector('.cart-drawer__sticky-checkout-count');
    const subtotalElement = stickyCheckoutBar.querySelector('.cart-drawer__sticky-checkout-subtotal');
    const summaryButton = stickyCheckoutBar.querySelector('.cart-drawer__sticky-checkout-summary');

    if (countElement) {
      countElement.textContent = countLabel;
    }

    if (subtotalElement) {
      subtotalElement.textContent = this.#cartSubtotal;
    }

    if (summaryButton instanceof HTMLButtonElement) {
      const summaryLabel = this.#cartSubtotal ? `${countLabel}, ${this.#cartSubtotal}` : countLabel;
      summaryButton.setAttribute('aria-label', `Open cart, ${summaryLabel}`);
    }
  }

  #handleViewportChange = () => {
    this.#setStickyCheckoutVisible(this.#shouldShowStickyCheckoutBar());
    this.#updateStickyCheckoutPosition();
  };

  #handleStickyCheckoutSummaryClick = (event) => {
    event.preventDefault();
    this.open();
  };

  #mountStickyCheckoutBar() {
    const stickyCheckoutBarCandidates = [...document.querySelectorAll('.cart-drawer__sticky-checkout')].filter(
      (candidate) => candidate instanceof HTMLDivElement
    );
    if (stickyCheckoutBarCandidates.length === 0) return;

    const mountedStickyCheckoutBar = stickyCheckoutBarCandidates.find((candidate) => candidate.parentElement === document.body);
    const componentStickyCheckoutBar = stickyCheckoutBarCandidates.find((candidate) => this.contains(candidate));
    const stickyCheckoutBar = mountedStickyCheckoutBar || componentStickyCheckoutBar || stickyCheckoutBarCandidates[0];
    if (!(stickyCheckoutBar instanceof HTMLDivElement)) return;

    for (const candidate of stickyCheckoutBarCandidates) {
      if (candidate === stickyCheckoutBar) continue;
      candidate
        .querySelector('.cart-drawer__sticky-checkout-summary')
        ?.removeEventListener('click', this.#handleStickyCheckoutSummaryClick);
      candidate.remove();
    }

    this.#stickyCheckoutBar = stickyCheckoutBar;
    const summaryButton = stickyCheckoutBar.querySelector('.cart-drawer__sticky-checkout-summary');
    summaryButton?.removeEventListener('click', this.#handleStickyCheckoutSummaryClick);
    summaryButton?.addEventListener('click', this.#handleStickyCheckoutSummaryClick);

    if (stickyCheckoutBar.parentElement !== document.body) {
      document.body.appendChild(stickyCheckoutBar);
    }

    this.#updateStickyCheckoutPosition();
  }

  #teardownStickyCheckoutBar() {
    const stickyCheckoutBar = this.#getStickyCheckoutBar();
    if (!stickyCheckoutBar) return;

    stickyCheckoutBar
      .querySelector('.cart-drawer__sticky-checkout-summary')
      ?.removeEventListener('click', this.#handleStickyCheckoutSummaryClick);
  }

  #observeStickyCheckoutBar() {
    if (this.#stickyCheckoutObserver) return;

    this.#stickyCheckoutObserver = new MutationObserver(() => {
      this.#mountStickyCheckoutBar();
      this.#syncStickyCheckoutBar();
      this.#setStickyCheckoutVisible(this.#shouldShowStickyCheckoutBar());
    });

    this.#stickyCheckoutObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  #observeViewportOverlays() {
    if (this.#overlayObserver || !document.body) return;

    this.#overlayObserver = new MutationObserver(() => {
      this.#updateStickyCheckoutPosition();
    });

    this.#overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open'],
    });
  }

  #updateStickyCheckoutPosition() {
    const stickyCheckoutBar = this.#getStickyCheckoutBar();
    if (!stickyCheckoutBar) return;

    stickyCheckoutBar.style.bottom = `${this.#getStickyCheckoutBottomOffset()}px`;
  }

  #getStickyCheckoutBottomOffset() {
    const baseOffset = 12;
    const overlaySelectors = [
      '#shopify-pc__banner.shopify-pc__banner__dialog',
      '.sticky-add-to-cart__bar[data-stuck="true"]',
      '.orthocloud-sticky-atc.is-active',
    ];

    return overlaySelectors.reduce((offset, selector) => {
      const overlay = document.querySelector(selector);
      if (!(overlay instanceof HTMLElement)) return offset;

      const styles = getComputedStyle(overlay);
      if (styles.display === 'none' || styles.visibility === 'hidden' || styles.opacity === '0') return offset;

      const rect = overlay.getBoundingClientRect();
      if (rect.height <= 0 || rect.top >= window.innerHeight || rect.bottom <= 0) return offset;

      return Math.max(offset, Math.round(window.innerHeight - rect.top + 12));
    }, baseOffset);
  }

  #resolveCartCount(event) {
    const explicitCount = event.detail.resource?.item_count;
    if (typeof explicitCount === 'number') return explicitCount;

    const eventItemCount = Number(event.detail.data?.itemCount);
    if (!Number.isNaN(eventItemCount)) {
      return event.detail.data?.source === 'product-form-component'
        ? Math.max(0, this.#cartCount + eventItemCount)
        : Math.max(0, eventItemCount);
    }

    return this.#readCartCountFromDom();
  }

  #setCartCount(cartCount) {
    this.#cartCount = Math.max(0, cartCount);
    this.#syncStickyCheckoutBar();

    if (this.#cartCount === 0) {
      this.#cartSubtotal = '';
      this.#setStickyCheckoutVisible(false);
    }
  }

  #readCartCountFromDom() {
    const countCandidates = [
      this.querySelector('[ref="cartItemCount"]'),
      document.querySelector('.header-actions__cart-icon .cart-bubble__text-count'),
      document.querySelector('.cart-drawer__heading .cart-bubble__text-count'),
    ];

    for (const candidate of countCandidates) {
      const rawValue = candidate?.textContent?.trim();
      if (!rawValue) continue;

      const parsedCount = Number.parseInt(rawValue.replace(/\D/g, ''), 10);
      if (!Number.isNaN(parsedCount)) return parsedCount;
    }

    return null;
  }

  #readCartSubtotalFromDom() {
    const subtotalCandidates = [this.querySelector('[data-cart-subtotal]'), document.querySelector('[data-cart-subtotal]')];

    for (const candidate of subtotalCandidates) {
      const rawValue = candidate?.textContent?.trim();
      if (rawValue) return rawValue.replace(/\s+/g, ' ');
    }

    return '';
  }

  #resolveCartSubtotal(event) {
    const explicitSubtotal = event.detail.resource?.total_price;
    if (typeof explicitSubtotal === 'number') return this.#formatMoney(explicitSubtotal);

    const sectionSubtotal = this.#readCartSubtotalFromSections(event.detail.data?.sections);
    if (sectionSubtotal) return sectionSubtotal;

    return this.#readCartSubtotalFromDom();
  }

  #readCartSubtotalFromSections(sections) {
    if (!sections || typeof sections !== 'object') return '';

    for (const sectionHtml of Object.values(sections)) {
      if (typeof sectionHtml !== 'string' || !sectionHtml.includes('data-cart-subtotal')) continue;

      const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const subtotalElement = sectionDocument.querySelector('[data-cart-subtotal]');
      const subtotalText = subtotalElement?.textContent?.trim();

      if (subtotalText) return subtotalText.replace(/\s+/g, ' ');
    }

    return '';
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
